import { BoxStaffRole } from "@/modules/boxStaff/gateway/boxStaffDto"
import { AddressDtoSchema } from "@modules/address/addressDto"
import { BiweeklyPeriod, PayoutFrequencyType, WeeklyAnchorStripe } from "@modules/box/boxPayoutFrequencyTypes"
import { PaymentMethodPreferenceDtoSchema } from "@modules/box/boxSettingsDto"
import { PhoneNumberWithPrefixSchema } from "@modules/box/boxTypes"
import { HUImageDtoSchema } from "@modules/dtos/imageDto"
import { TaxSettingsStatusSchema } from "@modules/finances/gateway/taxesDto"
import { StripeAccountErrorCode } from "@modules/finances/types/stripeErrorTypes"
import { StripeAccountStatus, StripePlatform } from "@modules/payment/stripeTypes"
import { UserDtoSchema } from "@modules/profile/userProfile/userDto"
import { LocationDtoSchema } from "@modules/types/locationTypes"
import { z } from "zod"

export const BoxDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    logo: HUImageDtoSchema.nullable(),
  })
  .meta({ id: "BoxDtoSchema" })

export type BoxDto = z.infer<typeof BoxDtoSchema>

export const BoxItemDtoSchema = z
  .object({
    id: z.string(),
    logo: HUImageDtoSchema,
    cover: HUImageDtoSchema,
    name: z.string(),
    address: AddressDtoSchema,
    phoneNumberWithPrefix: PhoneNumberWithPrefixSchema,
    email: z.string(),
    role: z.enum(BoxStaffRole),
  })
  .meta({ id: "BoxItemDtoSchema" })

export type BoxItemDto = z.infer<typeof BoxItemDtoSchema>

export const BoxWaiverDtoSchema = z
  .object({
    url: z.string(),
    key: z.string(),
    fileName: z.string(),
    originalFileName: z.string(),
    uploadedAt: z.string(),
    version: z.number(),
  })
  .meta({ id: "BoxWaiverDtoSchema" })

export type BoxWaiverDto = z.infer<typeof BoxWaiverDtoSchema>

export const BoxDetailDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    address: AddressDtoSchema,
    phoneNumberWithPrefix: PhoneNumberWithPrefixSchema,
    email: z.string(),
    owner: UserDtoSchema,
    logo: HUImageDtoSchema,
    cover: HUImageDtoSchema,
    photos: z.array(HUImageDtoSchema),
    siren: z.string(),
    description: z.string(),
    isSubscribed: z.boolean(),
    stripeAccountStatus: z.enum(StripeAccountStatus).nullable(),
    stripeAccountCurrency: z.string().nullable(),
    stripeMinimumAmount: z.number().nullable(),
    stripeMaximumAmount: z.number().nullable(),
    stripeAccountId: z.string().nullable(),
    stripePlatform: z.enum(StripePlatform),
    accountError: z.enum(StripeAccountErrorCode).nullable(),
    currentUserRole: z.enum(BoxStaffRole).nullable(),
    location: LocationDtoSchema.nullable(),
    slotGeneratorInWeeks: z.number().nullable(),
    paymentMethodPreferences: z.array(PaymentMethodPreferenceDtoSchema),
    zoneId: z.string().nullable(),
    waiver: BoxWaiverDtoSchema.nullable(),
    invitationLink: z.string().nullable(),
    showSessionAttendees: z.boolean(),
    taxStatus: TaxSettingsStatusSchema.nullable(),
    payoutFrequencyType: z.enum(PayoutFrequencyType),
    payoutFrequencyDayCalendar: z.number().nullable(),
    weeklyAnchorStripe: z.enum(WeeklyAnchorStripe).nullable(),
    biweeklyPeriod: z.enum(BiweeklyPeriod).nullable(),
    createdAt: z.string(),
  })
  .meta({ id: "BoxDetailDtoSchema" })

export type BoxDetailDto = z.infer<typeof BoxDetailDtoSchema>

export const WebBoxInputDtoSchema = z
  .object({
    name: z.string(),
    address: AddressDtoSchema,
    phoneNumberWithPrefix: PhoneNumberWithPrefixSchema,
    email: z.string(),
    logoKey: z.string().nullable(),
    coverKey: z.string().nullable(),
    imageKeys: z.array(z.string()).nullable(),
    siren: z.string(),
    description: z.string(),
    location: LocationDtoSchema.nullable(),
    zoneId: z.string().nullable(),
  })
  .meta({ id: "WebBoxInputDtoSchema" })

export type WebBoxInputDto = z.infer<typeof WebBoxInputDtoSchema>

export const InfosSubscriptionDtoSchema = z
  .object({
    subscriptionDate: z.string(),
    minimumCommitmentPeriod: z.boolean(),
    subscriptionEndDate: z.string(),
  })
  .meta({ id: "InfosSubscriptionDtoSchema" })

export type InfosSubscriptionDto = z.infer<typeof InfosSubscriptionDtoSchema>

export const BoxSettingsInputDtoSchema = z
  .object({
    paymentMethodPreferences: z.array(PaymentMethodPreferenceDtoSchema),
    slotGeneratorInWeeks: z.number(),
    showSessionAttendees: z.boolean(),
    payoutFrequency: z
      .object({
        type: z.enum(PayoutFrequencyType),
        dayCalendar: z.number().min(1).max(31).optional(),
        weeklyAnchorStripe: z.enum(WeeklyAnchorStripe).optional(),
        biweeklyPeriod: z.enum(BiweeklyPeriod).optional(),
      })
      .optional(),
  })
  .meta({ id: "BoxSettingsInputDtoSchema" })

export type BoxSettingsInputDto = z.infer<typeof BoxSettingsInputDtoSchema>

export const BoxSettingsWaiverDtoSchema = z
  .object({
    key: z.string(),
    filename: z.string(),
  })
  .nullable()
  .meta({ id: "BoxSettingsWaiverDtoSchema" })

export type BoxSettingsWaiverDto = z.infer<typeof BoxSettingsWaiverDtoSchema>
